import { useParams, useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, AlertCircle, Clock, ExternalLink, Globe, Info, Shield, User, Users } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { AuthService } from '@/services/core/auth'
import { UsersApi } from '@/services/users/usersApi'
import { AppShell } from '../../components/AppShell'
import { FormSkeleton } from '../../components/FormSkeleton'
import { Card, CardHeader, CardIcon, CardTitle, CardContent } from '@/components/ui/card'
import { ObjectTypeLabel } from '../../components/ObjectTypeLabel'
import { formatDate, formatTime } from '../../utils/format'
import { VersionHistoryButton } from '@/components/VersionHistoryButton'
import { ValidationWarnings } from '@/components/ValidationWarnings'

function formatStamp(iso?: string) {
  if (!iso) return '—'
  const d = new Date(iso)
  return `${formatDate(d)} ${formatTime(d)}`
}

interface UserEditPageProps {
  basePath: string
}

export function UserEditPage({ basePath }: UserEditPageProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const hasToken = AuthService.isAuthenticated()

  const { data: user, isLoading, error } = useQuery({
    queryKey: ['users', id],
    queryFn: () => UsersApi.getById(id!),
    enabled: hasToken && !!id,
  })

  if (isLoading) {
    return (
      <AppShell crumbs={[{ label: t('nav.users'), href: basePath }, { label: t('common.loading') }]} showBack>
        <FormSkeleton />
      </AppShell>
    )
  }

  if (error || !user) {
    return (
      <AppShell crumbs={[{ label: t('nav.users'), href: basePath }, { label: t('common.error') }]} showBack>
        <div className="ad-page">
          <div className="auth-form__error">
            <AlertCircle className="size-4" />
            {(error as Error)?.message ?? t('users.notFound')}
          </div>
        </div>
      </AppShell>
    )
  }

  const isActive = user.objectState === 'ACTIVE' && user.loginEnabled

  return (
    <AppShell
      crumbs={[{ label: t('nav.users'), href: basePath }, { label: user.displayName }]}
      showBack
      objectType={t('objectTypes.user')}
    >
      <div className="ad-page">
        <ValidationWarnings warnings={user?.validationWarnings} />
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <ObjectTypeLabel type={t('objectTypes.user')} />
            <h1>{user.displayName}</h1>
            <p>{user.username}</p>
          </div>
          <div className="ad-page__actions">
            <VersionHistoryButton />
            <Button variant="outline" onClick={() => navigate(basePath)}>
              <ArrowLeft />
              {t('common.back')}
            </Button>
          </div>
        </div>

        {/* Read-only notice */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 14px',
            borderRadius: 8,
            fontSize: 13,
            color: 'var(--ad-badge-blue, #2f6df4)',
            background: 'var(--ad-badge-blue-bg, rgba(47,109,244,0.08))',
            border: '1px solid var(--ad-badge-blue-border, rgba(47,109,244,0.2))',
          }}
        >
          <Info className="size-4" style={{ flexShrink: 0 }} />
          <span style={{ flex: 1 }}>{t('users.readOnlyNotice')}</span>
          <Button asChild variant="outline" size="sm">
            <a href="https://admin.adnuntius.com" target="_blank" rel="noopener noreferrer">
              <ExternalLink className="size-3.5" />
              admin.adnuntius.com
            </a>
          </Button>
        </div>

        {/* Details card */}
        <Card>
          <CardHeader>
            <CardTitle>
              <CardIcon icon={User} />
              {t('users.details')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="ad-form">
              <div className="ad-form__row">
                <label className="ad-form__label">{t('common.name')}</label>
                <div className="ad-form__value">{user.displayName}</div>
              </div>
              <div className="ad-form__row">
                <label className="ad-form__label">{t('users.username')}</label>
                <div className="ad-form__value" style={{ fontFamily: 'monospace', fontSize: 13 }}>{user.username}</div>
              </div>
              <div className="ad-form__row">
                <label className="ad-form__label">{t('common.status')}</label>
                <div className="ad-form__value">
                  <Badge variant={isActive ? 'success' : 'destructive'}>
                    {isActive ? t('common.active') : t('common.inactive')}
                  </Badge>
                </div>
              </div>
              <div className="ad-form__row">
                <label className="ad-form__label">{t('users.locale')}</label>
                <div className="ad-form__value">{user.locale}</div>
              </div>
              <div className="ad-form__row">
                <label className="ad-form__label">{t('users.twoFactor')}</label>
                <div className="ad-form__value">
                  <Badge variant={user.login2faRequired ? 'success' : 'warning'}>
                    {user.login2faRequired ? t('users.twoFactorEnabled') : user.twoFactorAuthType}
                  </Badge>
                </div>
              </div>
              <div className="ad-form__row">
                <label className="ad-form__label">{t('users.lastLogin')}</label>
                <div className="ad-form__value">{formatStamp(user.lastLogin)}</div>
              </div>
              <div className="ad-form__row">
                <label className="ad-form__label">{t('users.passwordExpiry')}</label>
                <div className="ad-form__value">
                  {user.passwordExpiry ? (
                    <>
                      {formatStamp(user.passwordExpiry)}
                      {user.passwordExpiresDays != null && (
                        <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--ad-text-muted)' }}>
                          ({user.passwordExpiresDays} {t('users.daysRemaining')})
                        </span>
                      )}
                    </>
                  ) : '—'}
                </div>
              </div>
              <div className="ad-form__row">
                <label className="ad-form__label">{t('common.id')}</label>
                <div className="ad-form__value" style={{ fontFamily: 'monospace', fontSize: 12 }}>{user.id}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Roles & Teams card */}
        <Card tone="people">
          <CardHeader>
            <CardTitle>
              <CardIcon icon={Shield} />
              {t('users.rolesAndTeams')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(user.userRoles?.rolesByNetwork ?? []).map((nr) => (
              <div key={nr.network.id} style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Globe className="size-3" style={{ color: 'var(--ad-text-muted)' }} />
                  {nr.network.name}
                </div>
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '6px 12px', borderBottom: '1px solid var(--ad-border)', color: 'var(--ad-text-muted)', fontWeight: 600, fontSize: 12 }}>
                        {t('users.role')}
                      </th>
                      <th style={{ textAlign: 'left', padding: '6px 12px', borderBottom: '1px solid var(--ad-border)', color: 'var(--ad-text-muted)', fontWeight: 600, fontSize: 12 }}>
                        {t('common.team')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {nr.roles.map((r, i) => (
                      <tr key={i}>
                        <td style={{ padding: '6px 12px', borderBottom: '1px solid var(--ad-border-subtle, var(--ad-border))' }}>
                          {r.role.name}
                          {r.role.application && (
                            <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--ad-text-faint)', textTransform: 'uppercase' }}>
                              {r.role.application}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '6px 12px', borderBottom: '1px solid var(--ad-border-subtle, var(--ad-border))' }}>
                          {r.team?.name ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                              <Users className="size-3" style={{ color: 'var(--ad-text-muted)' }} />
                              {r.team.name}
                              {r.team.type && (
                                <span style={{ fontSize: 10, color: 'var(--ad-text-faint)', textTransform: 'uppercase' }}>
                                  ({r.team.type})
                                </span>
                              )}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--ad-text-faint)' }}>—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Timestamps card */}
        <Card tone="insight">
          <CardHeader>
            <CardTitle>
              <CardIcon icon={Clock} />
              {t('stats.activity')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="ad-form">
              <div className="ad-form__row">
                <label className="ad-form__label">{t('stats.created')}</label>
                <div className="ad-form__value">{formatStamp(user.createTime)}</div>
              </div>
              <div className="ad-form__row">
                <label className="ad-form__label">{t('stats.updated')}</label>
                <div className="ad-form__value">{formatStamp(user.updateTime)}</div>
              </div>
              <div className="ad-form__row">
                <label className="ad-form__label">{t('users.lastPasswordChange')}</label>
                <div className="ad-form__value">{formatStamp(user.lastPasswordChange)}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  )
}
